if (!customElements.get('custom-form')) {
  class CustomForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.inputs = this.querySelectorAll('[data-input]');
      this.checkboxGroups = this.querySelectorAll('[data-checkbox]');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.requiredErrorMessages = this.querySelectorAll('[data-required-error]');
      this.invalidErrorMessages = this.querySelectorAll('[data-invalid-error]');
    }

    connectedCallback() {
      if (this.form && this.submitButton) {
        this.initializeValidation();
      }
    }

    initializeValidation() {
      // Add click listener to submit button to trigger validation
      this.submitButton.addEventListener('click', (e) => {
        this.validateForm(e);
      });

      // Add event listeners to remove error styling when user starts typing
      this.inputs.forEach((input, index) => {
        input.addEventListener('input', () => {
          this.validateInput(input, index);
          this.updateLabelPosition(input);
        });

        input.addEventListener('change', () => {
          this.validateInput(input, index);
          this.updateLabelPosition(input);
        });

        input.addEventListener('blur', () => {
          this.updateLabelPosition(input);
        });

        // Initialize label state on load
        this.updateLabelPosition(input);
      });

      // Add event listeners to checkboxes for multiple choice validation
      this.checkboxGroups.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          this.validateCheckboxGroup(checkbox);
        });
      });
    }

    validateForm(e) {
      let isValid = true;

      // Validate regular inputs
      this.inputs.forEach((input, index) => {
        if (!input.checkValidity()) {
          isValid = false;
          this.validateInput(input, index);
        } else {
          this.validateInput(input, index);
        }
      });

      // Validate checkbox groups
      this.validateAllCheckboxGroups();

      if (!isValid || !this.form.checkValidity()) {
        e.preventDefault();
        e.stopPropagation();

        // Focus on first error input
        const firstInvalidInput = [...this.inputs].find(input => !input.checkValidity());
        if (firstInvalidInput) {
          firstInvalidInput.focus();
        } else {
          // Check for invalid checkbox groups
          const invalidCheckboxGroup = this.getFirstInvalidCheckboxGroup();
          if (invalidCheckboxGroup) {
            invalidCheckboxGroup.focus();
          }
        }
      } else {
        // If no errors, submit the form
        if (e.type === 'click') {
          this.form.submit();
        }
      }
    }

    validateInput(input, index) {
      const validity = input.validity;

      if (!validity.valid) {
        this.addErrorStyling(input);

        if (validity.valueMissing) {
          this.showRequiredMessage(index);
          this.hideInvalidMessage(index);
        } else {
          this.hideRequiredMessage(index);
          this.showInvalidMessage(index);
        }
      } else {
        this.removeErrorStyling(input);
        this.hideRequiredMessage(index);
        this.hideInvalidMessage(index);
      }
    }

    validateCheckboxGroup(checkbox) {
      const groupId = checkbox.getAttribute('data-required-group');
      if (!groupId) return;

      const group = this.querySelectorAll(`[data-required-group="${groupId}"]`);
      const isAnyChecked = Array.from(group).some(cb => cb.checked);
      const fieldset = checkbox.closest('.legona-form__fieldset');
      const errorOutput = this.querySelector(`#LegonaForm-${groupId}-error`);

      if (!isAnyChecked) {
        fieldset?.setAttribute('data-error', 'true');
        errorOutput?.setAttribute('data-show', '');
      } else {
        fieldset?.removeAttribute('data-error');
        errorOutput?.removeAttribute('data-show');
      }
    }

    validateAllCheckboxGroups() {
      const processedGroups = new Set();
      this.checkboxGroups.forEach((checkbox) => {
        const groupId = checkbox.getAttribute('data-required-group');
        if (groupId && !processedGroups.has(groupId)) {
          processedGroups.add(groupId);
          this.validateCheckboxGroup(checkbox);
        }
      });
    }

    getFirstInvalidCheckboxGroup() {
      const processedGroups = new Set();
      for (const checkbox of this.checkboxGroups) {
        const groupId = checkbox.getAttribute('data-required-group');
        if (groupId && !processedGroups.has(groupId)) {
          processedGroups.add(groupId);
          const group = this.querySelectorAll(`[data-required-group="${groupId}"]`);
          const isAnyChecked = Array.from(group).some(cb => cb.checked);
          if (!isAnyChecked) {
            return checkbox;
          }
        }
      }
      return null;
    }

    addErrorStyling(input) {
      input.setAttribute('data-error', 'true');
      input.setAttribute('aria-invalid', 'true');
    }

    removeErrorStyling(input) {
      input.removeAttribute('data-error');
      input.setAttribute('aria-invalid', 'false');
    }

    updateLabelPosition(input) {
      const label = this.getLabelForinput(input);
      if (label) {
        // Special handling for date inputs
        if (input.type === 'date' && (input.value || input.matches(':focus'))) {
          label.setAttribute('data-active', 'true');
        } else if (input.type === 'date') {
          label.removeAttribute('data-active');
        } else if (input.value.trim()) {
          label.setAttribute('data-active', 'true');
        } else {
          label.removeAttribute('data-active');
        }
      }
    }

    getLabelForinput(input) {
      // Try to find label by for attribute matching input id
      if (input.id) {
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) return label;
      }

      // Try to find label as sibling with class legona-form__label
      const label = input.parentElement?.querySelector('.legona-form__label');
      if (label) return label;

      // Try to find label as previous sibling
      let sibling = input.previousElementSibling;
      while (sibling) {
        if (sibling.tagName === 'LABEL') {
          return sibling;
        }
        sibling = sibling.previousElementSibling;
      }

      return null;
    }

    showRequiredMessage(index) {
      this.requiredErrorMessages[index]?.setAttribute('data-show', '');
    }

    hideRequiredMessage(index) {
      this.requiredErrorMessages[index]?.removeAttribute('data-show');
    }

    showInvalidMessage(index) {
      this.invalidErrorMessages[index]?.setAttribute('data-show', '');
    }

    hideInvalidMessage(index) {
      this.invalidErrorMessages[index]?.removeAttribute('data-show');
    }
  }

  customElements.define('custom-form', CustomForm);
}